import { useCallback, useEffect, useRef, useState } from "react"
import type { PageInfo } from "./types"
import { PDF_VIEWER_BUFFER_PAGES } from "./config"
import { usePdfViewerStore } from "./store"


interface VisibleRange {
	start: number
	end: number
}

interface UseVirtualScrollResult {
	visibleRange: VisibleRange
	currentPage: number
}

export const useVirtualScroll = (
	scrollContainerRef: React.RefObject<HTMLDivElement | null>,
	pageInfos: PageInfo[],
	contentId: string
): UseVirtualScrollResult => {
	const setActivePage = usePdfViewerStore(s => s.setActivePage)
	const getActivePage = usePdfViewerStore(s => s.getActivePage)


	const [visibleRange, setVisibleRange] = useState<VisibleRange>({
		start: 0,
		end: 0,
	})
	const [currentPage, setCurrentPage] = useState(1)
	const hasRestoredPage = useRef(false)

	const calculateVisibleRange = useCallback(() => {
		const container = scrollContainerRef.current
		if (!container || pageInfos.length === 0) return

		const scrollTop = container.scrollTop
		const viewportHeight = container.clientHeight
		const viewportTop = scrollTop
		const viewportBottom = scrollTop + viewportHeight
		const viewportCenter = viewportTop + viewportHeight / 2

		let startIndex = 0
		let endIndex = pageInfos.length - 1
		let currentPageNumber = 1

		// Find first visible page
		for (let i = 0; i < pageInfos.length; i++) {
			const pageBottom = pageInfos[i].yPosition + pageInfos[i].height

			if (pageBottom >= viewportTop) {
				startIndex = Math.max(0, i - PDF_VIEWER_BUFFER_PAGES)

				// Determine current page by closest center to viewport center
				let closestDistance = Infinity
				for (let j = i; j < pageInfos.length; j++) {
					const pTop = pageInfos[j].yPosition
					const pBottom = pTop + pageInfos[j].height

					if (pBottom >= viewportTop && pTop <= viewportBottom) {
						const pCenter = pTop + pageInfos[j].height / 2
						const distance = Math.abs(pCenter - viewportCenter)
						if (distance < closestDistance) {
							closestDistance = distance
							currentPageNumber = pageInfos[j].pageNumber
						}
					}
				}
				break
			}
		}

		// Find last visible page
		for (let i = pageInfos.length - 1; i >= 0; i--) {
			if (pageInfos[i].yPosition <= viewportBottom) {
				endIndex = Math.min(pageInfos.length - 1, i + PDF_VIEWER_BUFFER_PAGES)
				break
			}
		}

		setVisibleRange({ start: startIndex, end: endIndex })
		setCurrentPage(currentPageNumber)
		// Persist current page to store
		if (contentId) {
			setActivePage(contentId, currentPageNumber)
		}
	}, [pageInfos, scrollContainerRef, contentId, setActivePage])

	// Restore saved page position on first layout
	useEffect(() => {
		if (hasRestoredPage.current || pageInfos.length === 0) return
		const container = scrollContainerRef.current
		if (!container) return

		const savedPage = getActivePage(contentId)
		if (savedPage && savedPage > 1) {
			const pageInfo = pageInfos.find(p => p.pageNumber === savedPage)
			if (pageInfo) {
				// Use instant scroll for restore — no animation needed
				container.scrollTo({ top: pageInfo.yPosition, behavior: "instant" })
			}
		}
		hasRestoredPage.current = true
	}, [pageInfos, contentId, getActivePage, scrollContainerRef])

	useEffect(() => {
		const container = scrollContainerRef.current
		if (!container) return

		calculateVisibleRange()

		const handleScroll = () => calculateVisibleRange()
		container.addEventListener("scroll", handleScroll, { passive: true })
		window.addEventListener("resize", calculateVisibleRange)

		return () => {
			container.removeEventListener("scroll", handleScroll)
			window.removeEventListener("resize", calculateVisibleRange)
		}
	}, [calculateVisibleRange, scrollContainerRef])

	return { visibleRange, currentPage }
}
